/**
 * CDSL Consolidated Account Statement (CAS) parser.
 *
 * CDSL emails a monthly CAS (password = PAN) covering every demat holding —
 * stocks and mutual-fund units held in demat — plus NPS. It is read on the
 * device like every other statement: the PDF and its password never leave it.
 *
 * Holdings rows look like (one text line per PDF row; names wrap onto the
 * lines around the ISIN line):
 *   INE208A01029 EQUITY SHARES OF RS 1/- EACH 206.000 -- -- -- 206.000 166.1000 34,216.60
 *   ISIN         [name part]  current-bal frozen pledge pledge-setup free-bal price value
 *
 * The holdings' values must add up to the statement's own demat "Portfolio
 * Value" line; a mismatch is reported so a layout change can't pass silently.
 */

import { readPdf } from "../pdf"
import type { PdfItems } from "../pdf-items"

export interface CasHolding {
  isin: string
  name: string
  kind: "stock" | "mutual_fund"
  units: number
  price: number
  value: number
}

export interface ParsedCas {
  source: "cdsl_cas"
  /** Statement date, YYYY-MM-DD */
  asOf: string | null
  holdings: CasHolding[]
  /** Demat total as printed by CDSL */
  dematValue: number | null
  npsValue: number | null
  totalValue: number | null
  /** False when the holdings don't add up to the printed demat total. */
  reconciled: boolean
}

const HOLDING_ROW =
  /^(IN[EF][A-Z0-9]{9})\s*(.*?)\s*([\d,]+\.\d{3})\s+\S+\s+\S+\s+\S+\s+\S+\s+([\d,]+\.\d{2,4})\s+([\d,]+\.\d{2})$/
const ISIN_START = /^IN[EF][A-Z0-9]{9}\b/
const AS_ON = /HOLDING STATEMENT AS ON (\d{2})-(\d{2})-(\d{4})/
const PORTFOLIO_VALUE = /Portfolio Value\s*`?\s*([\d,]+\.\d{2})\s+as on/
const TOTAL_VALUE = /Total Portfolio Value\s+([\d,]+\.\d{2})/

const num = (s: string) => Number(s.replace(/,/g, ""))

export function isCdslCas(text: string): boolean {
  return /Central Depository Services/i.test(text) && /CONSOLIDATED ACCOUNT STATEMENT/i.test(text)
}

/** Parse CAS text, one string per PDF row (top to bottom). */
export function parseCdslCasLines(lines: string[]): ParsedCas {
  let asOf: string | null = null
  let totalValue: number | null = null
  const portfolioValues: number[] = []
  const holdings: CasHolding[] = []

  lines.forEach((raw, i) => {
    const line = raw.trim()
    const asOn = AS_ON.exec(line)
    if (asOn && !asOf) asOf = `${asOn[3]}-${asOn[2]}-${asOn[1]}`
    const total = TOTAL_VALUE.exec(line)
    if (total && totalValue === null) totalValue = num(total[1])
    const pv = PORTFOLIO_VALUE.exec(line)
    if (pv) portfolioValues.push(num(pv[1]))

    const m = HOLDING_ROW.exec(line)
    if (!m) return
    const prev = lines[i - 1]?.trim() ?? ""
    const lead = prev && !ISIN_START.test(prev) && !/\d\.\d{2}$/.test(prev) ? prev : ""
    holdings.push({
      isin: m[1],
      name: `${lead} ${m[2]}`.replace(/#/g, " ").replace(/\s+/g, " ").trim(),
      kind: m[1].startsWith("INF") ? "mutual_fund" : "stock",
      units: num(m[3]),
      price: num(m[4]),
      value: num(m[5]),
    })
  })

  // First "Portfolio Value … as on" is the demat block; the next is NPS.
  const dematValue = portfolioValues[0] ?? null
  const npsValue = portfolioValues[1] ?? null
  const sum = Math.round(holdings.reduce((s, h) => s + h.value, 0) * 100) / 100
  return {
    source: "cdsl_cas",
    asOf,
    holdings,
    dematValue,
    npsValue,
    totalValue,
    reconciled: dematValue !== null && Math.abs(sum - dematValue) < 1,
  }
}

/** Parse a CDSL CAS PDF. Throws PdfPasswordError without the right password. */
export async function parseCdslCasPdf(data: ArrayBuffer, password?: string): Promise<ParsedCas> {
  return parseCdslCasLines(pdfTextLines(await readPdf(new Uint8Array(data), { password })))
}

/** Every page's text as visual lines (items grouped by y, ordered by x), from readPdf's items. */
export function pdfTextLines(items: PdfItems): string[] {
  const pages = new Map<number, Map<number, Array<[number, string]>>>()
  for (const it of items) {
    const rows = pages.get(it.page) ?? new Map<number, Array<[number, string]>>()
    pages.set(it.page, rows)
    rows.set(it.y, (rows.get(it.y) ?? []).concat([[it.x, it.str]]))
  }
  const lines: string[] = []
  for (const p of [...pages.keys()].sort((a, b) => a - b)) {
    const rows = pages.get(p) as Map<number, Array<[number, string]>>
    for (const y of [...rows.keys()].sort((a, b) => b - a)) {
      lines.push(
        (rows.get(y) ?? [])
          .sort((a, b) => a[0] - b[0])
          .map(([, s]) => s)
          .join(" ")
          .replace(/\s+/g, " ")
          .trim(),
      )
    }
  }
  return lines
}

export interface CasTrackedRow {
  isin: string
  name: string
  casValue: number
  paisaValue: number
  diff: number
}

/**
 * What a CAS would add, given the holdings Paisa already has. A row whose ISIN
 * (or a symbol that is an ISIN, e.g. Coin funds) is tracked is reported with
 * its value difference, not added again. NPS is added once if missing.
 */
export function planCasImport(
  cas: ParsedCas,
  holdings: Array<{
    name: string
    type: string
    symbol: string | null
    isin: string | null
    currentValue: number
  }>,
) {
  const byIsin = new Map<string, (typeof holdings)[number]>()
  for (const h of holdings) {
    for (const key of [h.isin, h.symbol]) if (key) byIsin.set(key.toUpperCase(), h)
  }
  const tracked: CasTrackedRow[] = []
  const toAdd: CasHolding[] = []
  for (const h of cas.holdings) {
    const existing = byIsin.get(h.isin)
    if (existing) {
      tracked.push({
        isin: h.isin,
        name: existing.name,
        casValue: h.value,
        paisaValue: existing.currentValue,
        diff: Math.round(h.value - existing.currentValue),
      })
    } else toAdd.push(h)
  }
  const hasNps = holdings.some((h) => h.type === "nps")
  const nps = cas.npsValue !== null && cas.npsValue > 0 && !hasNps ? cas.npsValue : null
  return { tracked, toAdd, nps }
}

/**
 * POST /investments/holdings bodies for a plan. The CAS has no cost basis, so
 * invested = current value until edited.
 */
export function casHoldingBodies(plan: ReturnType<typeof planCasImport>, owner: string) {
  const platform = "cdsl"
  const bodies: Array<Record<string, string>> = plan.toAdd.map((h) => ({
    name: h.name || h.isin,
    holdingType: h.kind,
    owner,
    platform,
    isin: h.isin,
    units: h.units.toFixed(4),
    investedAmount: h.value.toFixed(2),
    currentValue: h.value.toFixed(2),
  }))
  if (plan.nps !== null) {
    bodies.push({
      name: "NPS",
      holdingType: "nps",
      owner,
      platform,
      investedAmount: plan.nps.toFixed(2),
      currentValue: plan.nps.toFixed(2),
    })
  }
  return bodies
}
